/**
 * Compliance Filtering Utility
 * 
 * Optimized filtering functions with granular memoization for better performance.
 * Splits complex filtering logic into smaller, focused functions that can be 
 * memoized independently.
 */

import { type ComplianceIssue } from '../services/compliance.service';

export interface ComplianceFilters {
  types: string[];
  status: Array<'review' | 'fix' | 'ok'>;
  entityIds: string[];
}

/**
 * Filter issues by type (checkpoint codes like A1, A2, B1, etc.)
 */
export function filterByTypes(issues: ComplianceIssue[], types: string[]): ComplianceIssue[] {
  if (types.length === 0) return issues;
  
  return issues.filter(issue => {
    const issueType = issue.type || issue.issue_type;
    return issueType && types.includes(issueType);
  });
}


/**
 * Filter issues by status with legacy mapping support
 */
export function filterByStatus(
  issues: ComplianceIssue[], 
  statusFilters: Array<'review' | 'fix' | 'ok'>
): ComplianceIssue[] {
  if (statusFilters.length === 0) return issues;
  
  return issues.filter(issue => {
    if (!issue.status) return false;
    
    // Direct status matching
    if (statusFilters.includes(issue.status)) return true;
    
    // Handle legacy status mappings (for any remaining legacy data)
    // Use safe string conversion instead of type assertion
    const legacyStatus = String(issue.status || '');
    if (legacyStatus === 'active' && statusFilters.includes('review')) return true;
    if (legacyStatus === 'dismissed' && statusFilters.includes('ok')) return true;
    
    return false;
  });
}

/**
 * Filter issues by date range with timezone-safe comparisons
 */
export function filterByDateRange(
  issues: ComplianceIssue[], 
  dateRange: { start?: string; end?: string } | null
): ComplianceIssue[] {
  if (!dateRange || (!dateRange.start && !dateRange.end)) return issues;
  
  const startDate = dateRange.start ? new Date(dateRange.start) : null;
  const endDate = dateRange.end ? new Date(dateRange.end) : null;
  
  // Set end date to end of day for inclusive filtering
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
  }
  
  return issues.filter(issue => {
    if (!issue.source_transaction_date) return false;
    const issueDate = new Date(issue.source_transaction_date);
    
    // Handle all three cases: both dates, start-only, end-only
    if (startDate && endDate) {
      return issueDate >= startDate && issueDate <= endDate;
    } else if (startDate) {
      return issueDate >= startDate;  // All issues after start date
    } else if (endDate) {
      return issueDate <= endDate;    // All issues before end date
    }
    
    return true;
  });
}

/**
 * Filter issues by entity IDs
 */
export function filterByEntityIds(issues: ComplianceIssue[], entityIds: string[]): ComplianceIssue[] {
  if (entityIds.length === 0) return issues;
  
  return issues.filter(issue => entityIds.includes(issue.entity_id));
}

/**
 * Normalize text for search by handling HTML entities and whitespace
 * @param text - Text to normalize
 * @returns Normalized lowercase text
 */
function normalizeTextForSearch(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/&nbsp;/gi, ' ')      // HTML non-breaking space
    .replace(/&#32;/g, ' ')        // HTML encoded space
    .replace(/&#39;/g, "'")        // HTML encoded apostrophe
    .replace(/&amp;/g, '&')        // HTML encoded ampersand
    .replace(/&quot;/g, '"')       // HTML encoded quote
    .replace(/\s+/g, ' ')          // Normalize multiple spaces to single space
    .trim();
}

/**
 * Filter issues by search term across multiple fields
 * Optimized for performance with minimal overhead
 *
 * @param issues - Array of compliance issues to filter
 * @param searchTerm - Search string to match against issue fields
 * @returns Filtered array of issues matching the search term
 *
 * Note: Uses case-insensitive matching with HTML entity decoding and whitespace normalization
 * to match how text is displayed in the UI.
 */
export function filterBySearchTerm(issues: ComplianceIssue[], searchTerm: string): ComplianceIssue[] {
  try {
    // Fast path: no search term
    if (!searchTerm?.trim()) {
      return issues;
    }

    // Fast path: empty or invalid array
    if (!Array.isArray(issues) || issues.length === 0) {
      return [];
    }

    const startTime = import.meta.env.DEV ? performance.now() : 0;
    const searchLower = normalizeTextForSearch(searchTerm);

    // Optimized filter with early returns and minimal string operations
    const result = issues.filter(issue => {
      // Skip invalid issues
      if (!issue || typeof issue !== 'object') return false;

      // Check contact name first (most common search target)
      // Normalize to match display transformation
      const normalizedContact = normalizeTextForSearch(issue.contact_name);
      if (normalizedContact.includes(searchLower)) return true;

      // Check transaction reference (second most common)
      const normalizedRef = normalizeTextForSearch(issue.source_transaction_reference);
      if (normalizedRef.includes(searchLower)) return true;

      // Check title and description
      const normalizedTitle = normalizeTextForSearch(issue.title);
      if (normalizedTitle.includes(searchLower)) return true;

      const normalizedDesc = normalizeTextForSearch(issue.description);
      if (normalizedDesc.includes(searchLower)) return true;

      // Check line description (least common, check last)
      const lineDesc = issue.line_description || issue.metadata?.line_description || '';
      const normalizedLineDesc = normalizeTextForSearch(lineDesc);
      if (normalizedLineDesc.includes(searchLower)) return true;

      return false;
    });

    // Performance monitoring in development only
    if (import.meta.env.DEV) {
      const duration = performance.now() - startTime;
      if (duration > 50) {
        console.warn('[Performance] Slow search filter detected:', {
          duration: `${duration.toFixed(2)}ms`,
          issueCount: issues.length,
          resultCount: result.length,
          searchLength: searchTerm.length
        });
      }
    }

    return result;
  } catch (error) {
    // Graceful error handling - return unfiltered results
    console.error('[ComplianceFilter] Search failed:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      issueCount: issues?.length,
      searchTermLength: searchTerm?.length
    });
    // Return unfiltered on error for graceful degradation
    return Array.isArray(issues) ? issues : [];
  }
}

/**
 * Apply pagination to filtered results
 */
export function applyPagination<T>(
  items: T[], 
  currentPage: number, 
  pageSize: number
): {
  items: T[];
  totalFiltered: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
} {
  const totalFiltered = items.length;
  const totalPages = Math.ceil(totalFiltered / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalFiltered);
  const paginatedItems = items.slice(startIndex, endIndex);

  return {
    items: paginatedItems,
    totalFiltered,
    totalPages,
    hasNext: endIndex < totalFiltered,
    hasPrev: currentPage > 1
  };
}

/**
 * Optimized filtering pipeline with early returns for better performance
 */
export function applyCompleteFilter(
  issues: ComplianceIssue[],
  filters: ComplianceFilters,
  searchTerm: string = '',
  currentPage: number = 1,
  pageSize: number = 25
) {
  // Early return for empty data
  if (issues.length === 0) {
    return {
      issues: [],
      totalFiltered: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    };
  }

  // Apply filters in order of selectivity (most selective first for performance)
  let filtered = issues;
  
  // 1. Entity filter (most selective, but not needed for entity-specific pages)
  filtered = filterByEntityIds(filtered, filters.entityIds);
  
  // 2. Date range filter is now handled at the API level via global date parameters
  // filtered = filterByDateRange(filtered, filters.dateRange); // Removed - handled globally
  
  // 3. Search term filter (can be very selective)
  filtered = filterBySearchTerm(filtered, searchTerm);
  
  // 4. Type filters (moderately selective)
  filtered = filterByTypes(filtered, filters.types);
  
  // 5. Status filters (often not very selective)
  filtered = filterByStatus(filtered, filters.status);

  // 7. Apply pagination
  const paginated = applyPagination(filtered, currentPage, pageSize);
  
  return {
    issues: paginated.items,
    totalFiltered: paginated.totalFiltered,
    totalPages: paginated.totalPages,
    hasNext: paginated.hasNext,
    hasPrev: paginated.hasPrev
  };
}